import { FormDropzone } from '@form/FormDropzone';
import { FormMapping } from '@form/FormMapping';
import FormWatcher from '@form/FormWatcher';
import { useApi } from '@hooks/useApi';
import { Blockquote } from '@ui';
import { useClubSlug } from 'components/Clubs/hooks';
import { PricesAssistant, pricesSchema } from 'components/EventCalendar/PricesAssistant';
import { Category, Event } from 'components/EventCalendar/types';
import ConditionalFormPart from 'components/Form/base/ConditionalFormPart';
import { Form } from 'components/Form/base/Form';
import { FormDivider, FormInput, FormRow } from 'components/Form/next';
import Loader from 'components/Loader';
import { ExemptionsAssistent } from 'components/SpaceBooking/ExemptionsAssistent';
import { refTable } from 'components/TrainingPlan/TrainingPlanHelpers';
import AccountFormInput from 'components/UserInvoice/Account/AccountFormInput';
import CostCenterFormInput from 'components/UserInvoice/CostCenter/CostCenterFormInput';
import Joi from 'joi';
import { useCallback, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR, { useSWRConfig } from 'swr';
import { CheckSubscriptionBadge } from '~/components/Cashier';
import { ResourceResponse } from '../../../../types';
import { EventTrainerSelection } from './EventTrainerSelection';
import { RepeatingTypeSelect } from './RepeatingTypeSelect';

interface Props {
    category?: Category;
    event?: Event;
    eventId?: number;
    callback?: (data: Event) => any;
}

export const EventForm = ({ category, event: _event, eventId, callback }: Props) => {
    const { t } = useTranslation('bookings');
    const api = useApi();
    const clubSlug = useClubSlug();
    const [isLoading, setIsLoading] = useState(false);
    const times = refTable.times.map((time, index) => ({ key: index, label: time }));
    const { data: eventCategoryData, isLoading: isLoadingEventCategories } = useSWR<ResourceResponse<Category[]>>(
        `club/${clubSlug}/category`
    );
    const { data: categoryData, isLoading: isLoadingCategories } = useSWR<{
        data: Category[];
    }>(`club-member/${clubSlug}/category`);
    const { mutate: globalMutate } = useSWRConfig();
    const {
        data: eventData,
        isLoading: isLoadingEvent,
        mutate
    } = useSWR<{ data: Event }>(eventId ? `event/${eventId}` : false, null, {
        fallbackData: { data: _event }
    });
    const event = eventData?.data;

    const handleSubmit = useCallback(
        (data: FieldValues) => {
            setIsLoading(true);
            return api({
                url: event ? `event/${event.id}` : `club/${clubSlug}/event`,
                method: event ? 'PUT' : 'POST',
                data: {
                    ...data,
                    guidelines: Object.entries(data.guidelines || {})
                        .filter(([, value]) => value)
                        .map(([guideline]) => guideline)
                },
                onBefore: () => setIsLoading(false),
                onSuccess: ({ data: resData }) => {
                    void mutate(resData);
                    void globalMutate(`club/${clubSlug}/category`, null, { revalidate: true }); // update modified event categories
                    if (data.repeating_type === 'multi') alert(t('event.form.multiCreateAlert'));
                    callback?.(resData);
                }
            });
        },
        [event, clubSlug, callback, t]
    );

    const schema = Joi.object({
        form: Joi.number()
            .allow('', null)
            .label(t('event.form.formLabel'))
            .messages({ 'string.empty': t('event.form.formRequired') }),
        title: Joi.string().required().max(255).label(t('common.title')),
        start: Joi.date().allow('', null).label(t('event.form.startDateLabel')),
        deadline: Joi.date().allow('', null).label(t('event.form.deadlineHoursLabel')),
        cancellable_until: Joi.date().allow('', null).label(t('event.form.cancellableUntilHoursLabel')),
        trainers: Joi.array()
            .items(
                Joi.object({
                    id: Joi.number().required(),
                    notify: Joi.boolean(),
                    visible: Joi.boolean()
                })
            )
            .label(t('event.form.trainersLabel')),
        start_time: Joi.number().allow('', null).label(t('event.form.startTimeLabel')),
        end_time: Joi.number()
            .allow('', null)
            .greater(Joi.ref('start_time'))
            .label(t('event.form.endTimeLabel'))
            .messages({ 'number.greater': t('event.form.endTimeAfterStartError') }),
        repeat_period: Joi.string()
            .valid('', null, 'daily', 'weekly', 'monthly', 'yearly')
            // there is nothing to repeat without a first occurrence
            .when('start', {
                is: Joi.valid('', null),
                then: Joi.valid('', null).messages({ 'any.only': t('event.form.repeatRequiresStart') })
            })
            .label(t('event.form.repeatLabel')),
        repeat_until: Joi.date()
            .when('repeat_period', {
                is: Joi.string().required().invalid('', null),
                then: Joi.date()
                    .required()
                    .when('start', {
                        is: Joi.exist().invalid('', null),
                        then: Joi.date().greater(Joi.ref('start'))
                    }),
                otherwise: Joi.allow('', null)
            })
            .label(t('event.form.repeatUntilLabel')),
        requires_approval: Joi.boolean().allow('', null).label(t('event.form.requiresApprovalLabel')),
        max_participants: Joi.number().allow('', null).label(t('common.maxParticipants')),
        max_booking_lead_time: Joi.number().allow('', null).label(t('event.form.maxLeadTimeJoiLabel')),
        repeating_type: Joi.string()
            .required()
            .messages({ 'string.empty': t('event.form.repeatingTypeRequired') }),
        online_payments: Joi.boolean().allow('', null).label(t('event.form.onlinePaymentsLabel')),
        prices: pricesSchema,
        cancellable: Joi.boolean().allow('', null).label(t('event.form.cancellableJoiLabel'))
    }).unknown();

    return (
        <Loader isLoading={isLoadingEvent || isLoadingCategories || isLoadingEventCategories}>
            <Form
                onSubmit={handleSubmit}
                validationSchema={schema}
                defaultValues={{
                    visible: true,
                    repeating_type: 'single',
                    max_participants: 1,
                    start_time: '',
                    end_time: '',
                    ...event,
                    form_mapping: Array.isArray(event?.form_mapping) ? {} : event?.form_mapping,
                    trainers: event?.trainers?.map((trainer) => ({
                        id: trainer.id,
                        notify: trainer.notify ?? true,
                        visible: trainer.visible ?? true
                    })),
                    guidelines: event?.guidelines?.reduce((carry, guideline) => ({ ...carry, [guideline]: true }), {}),
                    category_id: event?.category_id || category?.id
                }}
            >
                <FormInput.Select
                    name="category_id"
                    label={t('event.form.categoryLabel')}
                    options={
                        eventCategoryData?.data.map((category) => ({
                            key: category.id,
                            label: category.title
                        })) || []
                    }
                />
                <FormInput name="title" label={t('common.title')} />
                <FormInput.WYSIWYG name="description" label={t('common.description')} />
                <FormInput.DatePicker name="start" label={t('event.form.startDateLabel')} />
                <FormRow>
                    <FormInput.Select
                        name="start_time"
                        label={t('event.form.startTimeLabel')}
                        options={[{ key: '', label: t('event.form.noTime') }, ...times]}
                    />
                    <FormInput.Select
                        name="end_time"
                        label={t('event.form.endTimeLabel')}
                        options={[{ key: '', label: t('event.form.noTime') }, ...times]}
                    />
                </FormRow>
                <FormInput.DatePicker
                    name="deadline"
                    granularity="minute"
                    label={t('event.form.deadlineLabel')}
                    helperText={t('event.form.deadlineHelperText')}
                />
                <FormInput.Checkbox name="cancellable" label={t('event.form.cancellableQuestionLabel')} />
                <FormInput.DatePicker
                    name="cancellable_until"
                    granularity="minute"
                    label={t('event.form.cancellableUntilLabel')}
                    helperText={t('event.form.cancellableUntilHelperText')}
                />
                <FormInput type="number" name="max_participants" label={t('common.maxParticipants')} />
                <FormInput type="number" name="max_bookings_per_email" label={t('common.maxBookingsPerEmail')} />
                <FormInput
                    type="number"
                    name="max_booking_lead_time"
                    min={0}
                    label={t('event.form.maxLeadTimeLabel')}
                    helperText={t('event.form.maxLeadTimeHelperText')}
                />
                <div className="tw-flex tw-flex-col">
                    <FormInput.Checkbox name="visible" spacing={8} label={t('event.form.visibleLabel')} />
                    <FormInput.Checkbox name="waiting_list" spacing={8} label={t('common.waitingListAllowed')} />
                    <FormInput.Checkbox
                        name="requires_approval"
                        spacing={8}
                        label={t('event.form.requiresApprovalLabel')}
                    />
                </div>
                <p className="tw-text-accents-6">{t('event.form.maxParticipantsExplainer')}</p>
                <p className="tw-text-accents-6">{t('event.form.requiresApprovalExplainer')}</p>
                <FormDivider collapsible title={t('event.form.trainersSectionTitle')} fieldKeys={['trainers']}>
                    <Blockquote className="!tw-mb-4 !tw-mt-0">{t('event.form.trainersBlockquote')}</Blockquote>
                    <EventTrainerSelection name="trainers" />
                </FormDivider>
                <FormDivider collapsible title={t('event.form.bookingPermissionsTitle')} fieldKeys={['guidelines']}>
                    <p className="tw-text-gray-500">{t('event.form.bookingPermissionsText')}</p>
                    <div>
                        <FormInput.Checkbox name={`guidelines.none`} label={t('event.form.guidelineNone')} />
                    </div>
                    <div>
                        <FormInput.Checkbox
                            name={`guidelines.member`}
                            label={
                                <span className="tw-flex tw-items-center">
                                    <span className="tw-mr-2 tw-inline-block">{t('event.form.guidelineMember')}</span>
                                    <CheckSubscriptionBadge
                                        size="xs"
                                        planKey="clubmembermanagementtool"
                                        hideWhenActive
                                    />
                                </span>
                            }
                        />
                    </div>
                    <div>
                        <FormInput.Checkbox name={`guidelines.relative`} label={t('event.form.guidelineRelative')} />
                    </div>
                    {categoryData?.data?.map((category) => (
                        <div key={category.id}>
                            <FormInput.Checkbox
                                name={`guidelines.category:${category.id}`}
                                label={t('event.form.guidelineCategoryMember', { category: category.title })}
                            />
                        </div>
                    ))}
                </FormDivider>
                <FormDivider
                    collapsible
                    title={t('event.form.recurringSectionTitle')}
                    fieldKeys={['repeat_period', 'repeat_until', 'repeating_type']}
                >
                    <FormRow>
                        <FormInput.Select
                            name="repeat_period"
                            label={t('event.form.repeatLabel')}
                            options={[
                                { label: t('event.form.repeatOption.none'), key: '' },
                                { label: t('event.form.repeatOption.daily'), key: 'daily' },
                                { label: t('event.form.repeatOption.weekly'), key: 'weekly' },
                                { label: t('event.form.repeatOption.monthly'), key: 'monthly' },
                                { label: t('event.form.repeatOption.yearly'), key: 'yearly' }
                            ]}
                        />
                        <FormInput.DatePicker name="repeat_until" label={t('event.form.repeatUntilLabel')} />
                    </FormRow>
                    <ConditionalFormPart name="repeat_period" condition={(value) => Boolean(value)}>
                        {!event && (
                            <>
                                <Blockquote spacing>{t('event.form.recurringTypesIntro')}</Blockquote>
                                <RepeatingTypeSelect name="repeating_type" />
                            </>
                        )}
                        <Blockquote spacing>{t('event.form.exemptionsBlockquote')}</Blockquote>
                        <ExemptionsAssistent name="exemptions" />
                    </ConditionalFormPart>
                </FormDivider>
                <FormDivider collapsible title={t('event.form.priceSettingsTitle')} fieldKeys={['prices']}>
                    <FormInput.Checkbox color="success" name="online_payments">
                        {t('event.form.onlinePaymentsLabel')}
                    </FormInput.Checkbox>
                    <PricesAssistant name="prices" />
                </FormDivider>
                <FormDivider collapsible title={t('event.form.invoiceInfoTitle')}>
                    <Blockquote className="tw-mb-4">{t('event.form.invoiceInfoBlockquote')}</Blockquote>
                    <CostCenterFormInput
                        name="invoice_settings.user_invoice_cost_1"
                        label={t('event.form.costCenter1')}
                    />
                    <CostCenterFormInput
                        name="invoice_settings.user_invoice_cost_2"
                        label={t('event.form.costCenter2')}
                    />
                    <AccountFormInput
                        name="invoice_settings.user_invoice_account_number"
                        label={t('event.form.account')}
                    />
                    <FormInput
                        type="number"
                        label={t('event.form.defaultVat')}
                        step={0.01}
                        min={0}
                        max={100}
                        name="invoice_settings.taxes"
                        placeholder="19"
                        labelRight="%"
                    />
                </FormDivider>
                <FormDivider collapsible title={t('event.form.filesSectionTitle')} fieldKeys={['files']}>
                    <FormDropzone name="files" valueMapping={(id) => ({ id })} />
                </FormDivider>
                <FormDivider title={t('event.form.formLabel')} collapsible defaultExpanded>
                    <Blockquote className="tw-mb-4">{t('event.form.formSectionBlockquote')}</Blockquote>
                    <FormInput.Formular name="form_id" />
                    {/* This mapping belongs to the event's invoice data. The
                        training planner owns its participant mapping in the
                        import preview, where the result is visible beside it. */}
                    <FormMapping formFieldName="form_id" multiple />
                </FormDivider>
                {!event && (
                    <FormWatcher name="form_id">
                        {(form_id) =>
                            !form_id ? (
                                <Blockquote color="warning">{t('event.form.noFormSelectedWarning')}</Blockquote>
                            ) : null
                        }
                    </FormWatcher>
                )}
                <FormInput.Submit isLoading={isLoading} />
            </Form>
        </Loader>
    );
};

export default EventForm;
